import React from "react";
import { useTranslation } from "react-i18next";

import type { IAlarmCentral, IOperatorSlim } from "App/Modules/Models";
import { Button } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { faHandshakeAlt } from "@fortawesome/pro-light-svg-icons";
import { faPen } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import "./_alarm-central-intro.scss";

type AlarmCentralIntroProps = {
    alarmCentral: IAlarmCentral;
    operators: IOperatorSlim[];
    isLoading: boolean;
    isAlarmCentralLoading: boolean;
    isOperatorsLoading: boolean;
    isEditAlarmCentralDialogOpen: (value: boolean) => void;
};

export const AlarmCentralIntro = (props: AlarmCentralIntroProps) => {
    const { t } = useTranslation();

    const {
        alarmCentral,
        operators,
        isLoading,
        isAlarmCentralLoading,
        isOperatorsLoading,
        isEditAlarmCentralDialogOpen,
    } = props;

    const handleEditAlarmCentral = () => {
        isEditAlarmCentralDialogOpen(true);
    };

    return isLoading && isAlarmCentralLoading && isOperatorsLoading ? (
        <div className="alarm-central-intro">
            <div>
                <h1 className="alarm-central-intro__header">
                    <Skeleton variant="text" animation="wave" height="30px" width="226px" />
                </h1>
            </div>
            <div className="alarm-central-intro__content">
                <div className="alarm-central-intro__content--half alarm-central-intro__section">
                    <p className="alarm-central-intro__partner alarm-central-intro__partner--skeleton">
                        <FontAwesomeIcon icon={faHandshakeAlt} /> &nbsp;
                        <Skeleton variant="text" animation="wave" height="13px" width="85px" />
                    </p>
                    <p className="alarm-central-intro__about">
                        <Skeleton variant="text" animation="wave" height="16px" width="100%" />
                        <Skeleton variant="text" animation="wave" height="16px" width="60%" />
                    </p>
                </div>
                <div className="alarm-central-intro__content--half alarm-central-intro__section alarm-central-intro__section--alt">
                    <div className="alarm-central-intro__data">
                        <span className="alarm-central-intro__caption">{t("Entities.AlarmCentral.Hostname")}</span>
                        <p className="alarm-central-intro__data-value">
                            <Skeleton variant="text" animation="wave" height="16px" width="150px" />
                        </p>
                    </div>
                    <div className="alarm-central-intro__data">
                        <span className="alarm-central-intro__caption">{t("Entities.AlarmCentral.Operators")}</span>
                        <p className="alarm-central-intro__data-value">
                            <Skeleton variant="text" animation="wave" height="16px" width="150px" />
                        </p>
                    </div>
                </div>
            </div>

            <Button
                className="alarm-central-intro__edit-button"
                variant="contained"
                size="large"
                startIcon={<FontAwesomeIcon icon={faPen} className="alarm-central-intro__edit-button__icon" />}
                disabled
            >
                {t("Gui.Buttons.Edit")}
            </Button>
        </div>
    ) : (
        <React.Fragment>
            {alarmCentral && operators ? (
                <div className="alarm-central-intro">
                    <div>
                        <h1 className="alarm-central-intro__header">{alarmCentral.name}</h1>
                        {alarmCentral.partner.name === null ? (
                            <p className="entity-list__empty-list">
                                <FontAwesomeIcon icon={faHandshakeAlt} />
                                &nbsp;
                                {t("Gui.Common.AlarmCentral.NoDescription")}
                            </p>
                        ) : (
                            <p className="alarm-central-intro__partner">
                                <FontAwesomeIcon icon={faHandshakeAlt} />
                                &nbsp;
                                {alarmCentral.partner.name}
                            </p>
                        )}
                    </div>

                    <div className="alarm-central-intro__content">
                        <div className="alarm-central-intro__content--half alarm-central-intro__section">
                            {alarmCentral.description === null ? (
                                <p className="entity-list__empty-list">{t("Gui.Common.AlarmCentral.NoDescription")}</p>
                            ) : (
                                <p className="alarm-central-intro__about">{alarmCentral.description}</p>
                            )}
                        </div>
                        <div className="alarm-central-intro__content--half alarm-central-intro__section alarm-central-intro__section--alt">
                            <div className="alarm-central-intro__data">
                                <span className="alarm-central-intro__caption">
                                    {t("Entities.AlarmCentral.Hostname")}
                                </span>
                                {alarmCentral.communicationConfiguration === null ? (
                                    <p className="entity-list__empty-list">
                                        {t("Gui.Common.AlarmCentral.NoInformation")}
                                    </p>
                                ) : (
                                    <p className="alarm-central-intro__data-value">
                                        {`https://${alarmCentral.communicationConfiguration.ip.externalAddress}:443`}
                                    </p>
                                )}
                            </div>

                            <div className="alarm-central-intro__data">
                                <span className="alarm-central-intro__caption">
                                    {t("Entities.AlarmCentral.Operators")}
                                </span>
                                <ul className="alarm-central-intro__data-list">
                                    {operators?.map((operator, index) => {
                                        return (
                                            <li className="alarm-central-intro__data-list-item" key={index}>
                                                {operator === null ? (
                                                    <p className="entity-list__empty-list">
                                                        {t("Gui.Common.AlarmCentral.NoInformation")}
                                                    </p>
                                                ) : (
                                                    <p className="alarm-central-intro__data-value">
                                                        {`- ${operator.partnerUser.displayName}`}
                                                    </p>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </div>
                    </div>

                    <Button
                        className="alarm-central-intro__edit-button"
                        variant="contained"
                        color="primary"
                        size="large"
                        startIcon={<FontAwesomeIcon icon={faPen} className="alarm-central-intro__edit-button__icon" />}
                        onClick={handleEditAlarmCentral}
                    >
                        {t("Gui.Buttons.Edit")}
                    </Button>
                </div>
            ) : null}
        </React.Fragment>
    );
};
